import { Component, inject } from '@angular/core';
import { DatePipe } from '@angular/common';

import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PlanReviewerService } from '../../../../../shared/plan-reviewer.service';
import { getChapterUrlSlug } from '../../../../../shared/utils/icc-url-utils';

export interface RationaleDialogData {
  pageNumber: number;
  pageTitle: string;
  sectionTitle: string;
  sectionId: string;
  hierarchy: string;
  hierarchyArr: { title: string; id?: string; chapterSlug?: string }[];
  relevance: string;
  rationale: string;
  codeBook?: any;
  applicabilityModel?: string;
  applicabilityCreatedTime?: Date;
}

/** Modal dialog showing the detailed compliance rationale and statement for a single building-code section. */
@Component({
  selector: 'app-rationale-dialog',
  imports: [MatDialogModule, MatButtonModule, DatePipe],
  templateUrl: './rationale-dialog.component.html',
  styleUrls: ['./rationale-dialog.component.scss'],
})
export class RationaleDialogComponent {
  dialogRef = inject<MatDialogRef<RationaleDialogComponent>>(MatDialogRef);
  data = inject<RationaleDialogData>(MAT_DIALOG_DATA);
  private sanitizer = inject(DomSanitizer);
  private planReviewerService = inject(PlanReviewerService);

  iccUrl: string | null = null;
  hierarchyWithLinks: SafeHtml;

  constructor() {
    const data = this.data;

    const codeBook = data.codeBook || this.planReviewerService.getCodeBookFromSectionId(data.sectionId);
    const documentSlug = codeBook.document_slug;

    // Generate URL slug from chapter title (handles Code Connect API chapter IDs)
    const chapterItem = data.hierarchyArr.length > 1 ? data.hierarchyArr[1] : null;
    const urlSlug = chapterItem ? getChapterUrlSlug(chapterItem.title, chapterItem.chapterSlug) : '';

    // Build ICC URL from the hierarchy - handle new structure with Book at index 0
    if (data.hierarchyArr.length === 1) {
      // This is the book level - link to base URL
      this.iccUrl = `https://codes.iccsafe.org/content/${documentSlug}`;
    } else if (data.hierarchyArr.length === 2) {
      // This is the chapter level - link to chapter URL without fragment
      if (urlSlug) {
        this.iccUrl = `https://codes.iccsafe.org/content/${documentSlug}/${urlSlug}`;
      }
    } else if (data.hierarchyArr.length > 2 && urlSlug && data.sectionId) {
      // This is a section level - link to section within chapter
      this.iccUrl = `https://codes.iccsafe.org/content/${documentSlug}/${urlSlug}#${data.sectionId}`;
    }

    // Build hierarchy with links (exclude the leaf/current section, same as compliance table)
    const hierarchyArrNoLeaf = data.hierarchyArr.slice(0, -1);
    const hierarchyHtml = hierarchyArrNoLeaf
      .map((h, index) => {
        if (h.id) {
          let url: string;

          if (index === 0) {
            // Book level - link to base URL
            url = `https://codes.iccsafe.org/content/${documentSlug}`;
          } else if (index === 1 && urlSlug) {
            // Chapter level - link to chapter URL without fragment
            url = `https://codes.iccsafe.org/content/${documentSlug}/${urlSlug}`;
          } else if (index > 1 && urlSlug) {
            // Section level - link to section within chapter
            url = `https://codes.iccsafe.org/content/${documentSlug}/${urlSlug}#${h.id}`;
          } else {
            // Fallback - just return title without link
            return h.title;
          }

          return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="hierarchy-link">${h.title}</a>`;
        } else {
          return h.title;
        }
      })
      .join(' <span class="hierarchy-sep">&gt;</span> ');

    this.hierarchyWithLinks = this.sanitizer.bypassSecurityTrustHtml(hierarchyHtml);
  }
}
